"use client";

import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Check, Copy, Gift, Send } from "lucide-react";
import { plumeAPI } from "@/src/helpers/ServiceHelper";
import { fetchReferrals, getReferralLink, sendReferralInvitation } from "@/src/helpers/dbFunctions";
import { captureEvent } from "@/src/helpers/trackEvent";

const STATUS_LABEL = {
  pending: "invited",
  signed_up: "signed up",
  upgraded: "rewarded",
};

function StatusPill({ status }) {
  const rewarded = status === "upgraded";
  return (
    <span
      className="qk-caps shrink-0 rounded-full px-2 py-0.5"
      style={{
        fontSize: 9,
        background: rewarded ? "color-mix(in srgb, var(--qk-lime) 25%, transparent)" : "var(--qk-paper-3)",
        border: "1px solid var(--qk-rule)",
        color: rewarded ? "var(--qk-ink)" : "var(--qk-muted)",
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function ReferralContent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [link, setLink] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);

  // Deliberately not a useCallback dependency of the mount effect: Auth0's
  // helpers get fresh identities each render, which would re-fire the load in a
  // loop.
  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [referralLink, list] = await Promise.all([
        getReferralLink({ getAccessTokenSilently, logout, plumeAPI }),
        fetchReferrals({ getAccessTokenSilently, logout, plumeAPI }),
      ]);
      if (!referralLink) throw new Error("referral link missing from response");
      setLink(referralLink);
      setReferrals(Array.isArray(list) ? list : []);
    } catch (error) {
      // No local link fallback: the old UI fabricated a code the backend never
      // issued, so users copied dead links and saw no error.
      console.error("Could not load referrals", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      captureEvent("referral_link_copied", { code: link.split("referral_code=")[1] });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setNotice({ tone: "error", text: "Couldn't copy — select the link and copy manually." });
    }
  };

  const invite = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setSending(true);
    setNotice(null);
    try {
      await sendReferralInvitation({ getAccessTokenSilently, logout, plumeAPI, email: address });
      captureEvent("referral_invite_sent");
      setEmail("");
      setNotice({ tone: "ok", text: `Invitation sent to ${address}.` });
      load();
    } catch (error) {
      const code = error?.response?.data?.code;
      const message =
        code === "SELF_REFERRAL_NOT_ALLOWED"
          ? "You can't refer yourself."
          : code === "USER_ALREADY_EXISTS"
            ? "That person already has a Quarkle account."
            : error?.response?.data?.error || "Couldn't send that invitation.";
      captureEvent("referral_invite_failed", { reason: code || "unknown" });
      setNotice({ tone: "error", text: message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto pt-12">
      <div className="mb-4 flex items-center gap-2 px-2">
        <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
          refer &amp; earn
        </span>
        <div className="h-px flex-1" style={{ background: "var(--qk-rule)" }} />
      </div>

      <div
        className="mx-2 mb-4 rounded-xl p-4"
        style={{ background: "var(--qk-paper-3)", border: "1px solid var(--qk-rule)" }}
      >
        <div className="mb-2 flex items-center gap-2">
          <Gift className="h-4 w-4" style={{ color: "var(--qk-ink)" }} />
          <span className="qk-display" style={{ fontSize: 16, fontStyle: "italic", color: "var(--qk-ink)" }}>
            Give 14 days, get a month
          </span>
        </div>
        <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--qk-muted)" }}>
          Your friend gets 14 days of Quarkle Pro free. When they become a paying subscriber, you get a
          $19.99 credit — a month of Pro on us.
        </p>
      </div>

      {loading ? (
        <p className="px-4" style={{ fontSize: 12, color: "var(--qk-muted)" }}>
          Loading your referrals…
        </p>
      ) : loadError ? (
        <div className="px-4">
          <p style={{ fontSize: 12, color: "var(--qk-clay)" }}>Couldn&apos;t load your referral link.</p>
          <button onClick={load} className="qk-caps mt-2 underline" style={{ fontSize: 10, color: "var(--qk-ink)" }}>
            try again
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 px-2">
            <label className="qk-caps mb-1.5 block px-1" style={{ fontSize: 10, color: "var(--qk-muted)" }}>
              your link
            </label>
            <div className="flex items-center gap-1.5">
              <input
                readOnly
                value={link}
                onFocus={(event) => event.target.select()}
                className="qk-mono min-w-0 flex-1 truncate rounded-lg px-2.5 py-2"
                style={{
                  fontSize: 11,
                  background: "var(--qk-paper-3)",
                  border: "1px solid var(--qk-rule)",
                  color: "var(--qk-ink)",
                }}
              />
              <button
                type="button"
                onClick={copyLink}
                aria-label="Copy referral link"
                className="qk-icon-button shrink-0 rounded-lg p-2"
                style={{ border: "1px solid var(--qk-rule)" }}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" style={{ color: "var(--qk-ink)" }} />
                ) : (
                  <Copy className="h-3.5 w-3.5" style={{ color: "var(--qk-ink)" }} />
                )}
              </button>
            </div>
          </div>

          <form onSubmit={invite} className="mb-4 px-2">
            <label className="qk-caps mb-1.5 block px-1" style={{ fontSize: 10, color: "var(--qk-muted)" }}>
              invite by email
            </label>
            <div className="flex items-center gap-1.5">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="friend@example.com"
                className="min-w-0 flex-1 rounded-lg px-2.5 py-2"
                style={{
                  fontSize: 12,
                  background: "var(--qk-paper-3)",
                  border: "1px solid var(--qk-rule)",
                  color: "var(--qk-ink)",
                }}
              />
              <button
                type="submit"
                disabled={sending || !email.trim()}
                aria-label="Send invitation"
                className="qk-icon-button shrink-0 rounded-lg p-2 disabled:opacity-40"
                style={{ border: "1px solid var(--qk-rule)" }}
              >
                <Send className="h-3.5 w-3.5" style={{ color: "var(--qk-ink)" }} />
              </button>
            </div>
            {notice && (
              <p
                className="mt-2 px-1"
                style={{ fontSize: 11, color: notice.tone === "ok" ? "var(--qk-muted)" : "var(--qk-clay)" }}
              >
                {notice.text}
              </p>
            )}
          </form>

          <div className="mb-2 flex items-center gap-2 px-2">
            <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
              your referrals
            </span>
            <div className="h-px flex-1" style={{ background: "var(--qk-rule)" }} />
          </div>
          <div className="flex flex-col gap-1 px-3 pb-4">
            {referrals.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--qk-muted)" }}>
                No invitations yet. Share your link to get started.
              </p>
            ) : (
              referrals.map((referral) => (
                <div key={referral.id} className="flex items-center gap-2 py-1.5">
                  <span className="qk-mono min-w-0 flex-1 truncate" style={{ fontSize: 11, color: "var(--qk-ink)" }}>
                    {referral.email}
                  </span>
                  <StatusPill status={referral.status} />
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
